import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, CheckCircle2, Clock, XCircle, TrendingUp, ArrowRight } from "lucide-react";
import api from "@/lib/api";

const STATUS_LABELS = {
    novo: "Novo",
    em_analise: "Em análise",
    orcamento_enviado: "Orçamento enviado",
    aceite: "Aceite",
    agendado: "Agendado",
    concluido: "Concluído",
    cancelado: "Cancelado",
};

export default function AdminOverview() {
    const [stats, setStats] = useState(null);
    const [recent, setRecent] = useState([]);

    useEffect(() => {
        (async () => {
            const [s, r] = await Promise.all([
                api.get("/admin/stats"),
                api.get("/admin/quotes"),
            ]);
            setStats(s.data);
            setRecent(r.data.slice(0, 5));
        })();
    }, []);

    if (!stats) return <div>A carregar...</div>;

    return (
        <div className="space-y-8" data-testid="admin-overview">
            <div>
                <h1 className="font-heading text-3xl font-bold tracking-tight">Visão geral</h1>
                <p className="text-white/60">Estado atual dos pedidos de orçamento.</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat label="Total de pedidos" value={stats.total} icon={Inbox} color="bg-[#C9A961]/10 text-[#C9A961]" />
                <Stat label="Novos" value={stats.novo} icon={TrendingUp} color="bg-blue-50 text-blue-600" />
                <Stat label="Em análise" value={stats.em_analise} icon={Clock} color="bg-amber-50 text-amber-600" />
                <Stat label="Concluídos" value={stats.concluido} icon={CheckCircle2} color="bg-emerald-50 text-emerald-600" />
            </div>

            <div className="ab-card p-6 lg:p-8">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="font-heading text-xl font-bold">Pedidos recentes</h2>
                    <Link to="/admin/pedidos" className="text-sm text-[#C9A961] hover:underline flex items-center gap-1" data-testid="view-all-quotes">
                        Ver todos <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                </div>
                {recent.length === 0 ? (
                    <div className="text-white/50 text-center py-8">Ainda não há pedidos.</div>
                ) : (
                    <div className="divide-y divide-[#2A2A2A]">
                        {recent.map((q) => (
                            <Link key={q.id} to={`/admin/pedidos/${q.id}`} className="flex items-center justify-between py-3 hover:bg-[#171717] -mx-2 px-2 rounded-lg">
                                <div>
                                    <div className="font-mono text-sm text-white/50">{q.reference}</div>
                                    <div className="font-medium">{q.customer?.name} · {q.customer?.email}</div>
                                </div>
                                <div className="text-right">
                                    <div className="font-heading font-bold">{q.calculation?.total?.toFixed(2)} €</div>
                                    <StatusBadge status={q.status} />
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function Stat({ label, value, icon: Icon, color }) {
    return (
        <div className="ab-card p-6">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
                <Icon className="w-5 h-5" />
            </div>
            <div className="mt-4 font-heading font-black text-3xl">{value}</div>
            <div className="text-sm text-white/50 mt-1">{label}</div>
        </div>
    );
}

export function StatusBadge({ status }) {
    const map = {
        novo: "bg-blue-500/15 text-blue-300",
        em_analise: "bg-amber-500/15 text-amber-300",
        orcamento_enviado: "bg-purple-500/15 text-purple-300",
        aceite: "bg-emerald-500/15 text-emerald-300",
        agendado: "bg-indigo-500/15 text-indigo-300",
        concluido: "bg-green-500/15 text-green-300",
        cancelado: "bg-red-500/15 text-red-300",
    };
    return <span className={`ab-badge-status ${map[status] || "bg-white/10 text-white/70"}`}>{STATUS_LABELS[status] || status}</span>;
}
